import React, { useState, useEffect } from "react";
import {
  Tabs, Tab, Form, Button, Table, Col, Row, Card, Alert, Badge, Modal,
  ProgressBar, Accordion, InputGroup, Toast, ToastContainer, Spinner
} from "react-bootstrap";
import {
  FaShieldAlt, FaFileDownload, FaPlus, FaEdit, FaTrash, FaCheck, FaTimes,
  FaExclamationTriangle, FaGlobe, FaLock, FaUserShield, FaKey
} from "react-icons/fa";

import { FaClock } from "react-icons/fa";

import CCPACrossBorderTransfersTab from "./CCPAOptOutConsent";
import CCPAPolicyGeneratorTab from "./CCPAPolicyGeneratorTab";
import CCPADsarManagementTab from "./CCPADsarManagementTab";
import CCPAInventoryTab from "./CCPAInventoryTab";
import CCPARetentionTab from "./CCPARetentionTab";
import CCPASecurityEncryptionTab from "./CCPASecurityEncryptionTab";
import Menubar from "../Menubar";
import BackgroundFooter from "../BackgroundFooter";
import "../../ComponentsCss/CCPA.css"
import NavigationBar from "../Nav";

const SUBSCRIPTION_ENABLED = false;

const CCPAModule = ({ userData }) => {

  const [activeKey, setActiveKey] = useState("dashboard");
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastVariant, setToastVariant] = useState("success");
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [recentActivities, setRecentActivities] = useState([]);
  const [complianceMetrics, setComplianceMetrics] = useState({});

  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(true);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [trialEndDate, setTrialEndDate] = useState(null);
  const [isExpired, setIsExpired] = useState(false);
  const [checkingOutPayment, setCheckingOutPayment] = useState(false);
  const [secondTrialUsed, setSecondTrialUsed] = useState(false);
  const [subscriptionKey, setSubscriptionKey] = useState('');
  const [isActivatingKey, setIsActivatingKey] = useState(false);
  const [keyValidationError, setKeyValidationError] = useState('');

  const getUserRegion = () => sessionStorage.getItem("userRegion") || "GB";

  const fetchRecentActivities = async () => {
    const userId = userData?.["cognito:username"];
    const region = getUserRegion();

    try {
      const res = await fetch("https://cthceyv68h.execute-api.ap-southeast-2.amazonaws.com/prod/api/ccpa/activities/list", {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "Region": region,
          "UserId": userId
        }
      });

      const data = await res.json();
      setRecentActivities(data);

    } catch (err) {
      console.error("Error fetching activities", err);
    }
  };

  useEffect(() => {
    fetchRecentActivities();
  }, [userData]);

  const handleSidebarToggle = () => setIsSidebarCollapsed((prev) => !prev);

  useEffect(() => {
    localStorage.setItem("ccpaIsSidebarCollapsed", JSON.stringify(isSidebarCollapsed));
  }, [isSidebarCollapsed]);

  const showNotification = (message, variant = "success") => {
    setToastMessage(message);
    setToastVariant(variant);
    setShowToast(true);
  };

  // DISABLE SUBSCRIPTION CHECK
  useEffect(() => {

    if (!SUBSCRIPTION_ENABLED) {
      setSubscriptionStatus("active");
      setSubscriptionLoading(false);
      return;
    }

  }, [userData]);

  const renderSubscriptionBanner = () => {

    if (!SUBSCRIPTION_ENABLED) return null;

    if (subscriptionStatus === 'trialing' && trialEndDate) {

      const daysLeft = Math.floor((new Date(trialEndDate) - new Date()) / (1000 * 60 * 60 * 24));
      const isUrgent = daysLeft <= 3;

      return (
        <Alert variant={isUrgent ? "danger" : "warning"} className="mb-4 trialWarning">
          <div className="d-flex justify-content-between align-items-center gap-2">
            <div>
              <FaClock className="me-2" />
              <strong>Trial Active:</strong> {daysLeft} days remaining
            </div>
          </div>
        </Alert>
      );
    }

    if (subscriptionStatus === 'active') {
      return (
        <Alert variant="success" className="mb-4">
          <FaCheck className="me-2" />
          <strong>Subscription Active</strong>
        </Alert>
      );
    }

    return null;
  };

  const disableRightClick = (e) => {
    e.preventDefault();
  };

  if (SUBSCRIPTION_ENABLED && subscriptionLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '60vh' }}>
        <Spinner animation="border" variant="primary" />
        <span className="ms-3">Checking subscription...</span>
      </div>
    );
  }

  return (

    <div onContextMenu={disableRightClick}>

      <Row className="g-0">

        <Col
          xs={12}
          className={`bg-transparent sidebar-container ${isSidebarCollapsed ? "collapsed" : "hello"}`}
          style={{
            width: isSidebarCollapsed ? "90px" : "317px",
            transition: "width 0.3s ease",
          }}
        >

          <Menubar
            isCollapsed={isSidebarCollapsed}
            onToggle={handleSidebarToggle}
          />

        </Col>

        <Col
          className="pt-0 px-0 ps-0 section-container"
          style={{
            width: `calc(100% - ${isSidebarCollapsed ? "90px" : "317px"})`,
            transition: "width 0.3s ease",
          }}
        >

          <NavigationBar
            userData={userData}
            isSidebarCollapsed={isSidebarCollapsed}
          />

          <Col className="overflow-auto p-4">

            {renderSubscriptionBanner()}

            <Tabs
              id="ccpa-module-tabs"
              activeKey={activeKey}
              onSelect={setActiveKey}
              className="mb-3"
            >

              <Tab tabClassName="dashboard2" eventKey="dashboard" title="Dashboard">

                <Card>
                  <Card.Body>

                    <Card.Title>CCPA Compliance Dashboard</Card.Title>

                    <Row>

                      <Col md={4}>

                        <h5>Compliance Overview</h5>

                        <ProgressBar
                          label={`Overall: ${complianceMetrics.overallScore || 0}%`}
                          now={complianceMetrics.overallScore || 0}
                          variant="success"
                        />

                      </Col>

                      <Col md={8}>

                        <h5>Recent Activities</h5>

                        <Table hover size="sm">

                          <thead>
                            <tr>
                              <th>Date</th>
                              <th>Action</th>
                              <th>Status</th>
                            </tr>
                          </thead>

                          <tbody>

                            {recentActivities.length === 0 ?

                              <tr>
                                <td colSpan={3}>No activities found</td>
                              </tr>

                              :

                              recentActivities.map((act, i) => (

                                <tr key={i}>

                                  <td>{act.timestamp ? new Date(act.timestamp).toLocaleString() : ""}</td>

                                  <td>{act.description}</td>

                                  <td>
                                    <Badge bg={act.action || "info"}>
                                      {act.action}
                                    </Badge>
                                  </td>

                                </tr>

                              ))

                            }

                          </tbody>

                        </Table>

                      </Col>

                    </Row>

                  </Card.Body>
                </Card>

              </Tab>

              <Tab eventKey="inventory" title="Data Inventory">
                <CCPAInventoryTab
                  userData={userData}
                  showNotification={showNotification}
                  region={getUserRegion()}
                  refreshActivities={fetchRecentActivities}
                />
              </Tab>

              <Tab eventKey="retention" title="Retention">
                <CCPARetentionTab
                  userData={userData}
                  showNotification={showNotification}
                  region={getUserRegion()}
                  refreshActivities={fetchRecentActivities}
                />
              </Tab>

              <Tab eventKey="dsar" title="Consumer Requests">
                <CCPADsarManagementTab
                  userData={userData}
                  showNotification={showNotification}
                  region={getUserRegion()}
                  refreshActivities={fetchRecentActivities}
                />
              </Tab>

              <Tab eventKey="policygen" title="Policy Generator">
                <CCPAPolicyGeneratorTab
                  userData={userData}
                  showNotification={showNotification}
                  region={getUserRegion()}
                  refreshActivities={fetchRecentActivities}
                />
              </Tab>

              <Tab eventKey="security" title="Security Measures">
                <CCPASecurityEncryptionTab
                  userData={userData}
                  showNotification={showNotification}
                  region={getUserRegion()}
                  refreshActivities={fetchRecentActivities}
                />
              </Tab>

            </Tabs>

          </Col>

          <BackgroundFooter isSidebarCollapsed={isSidebarCollapsed} />

        </Col>

      </Row>

      <ToastContainer position="bottom-end" className="p-3">

        <Toast
          show={showToast}
          onClose={() => setShowToast(false)}
          bg={toastVariant}
          autohide
          delay={2500}
        >

          <Toast.Body>{toastMessage}</Toast.Body>

        </Toast>

      </ToastContainer>

    </div>

  );
};

export default CCPAModule;